import React, { useState, useEffect } from 'react';
import EventSidebarMenu from './EventSidebarMenu';
import SelectOrCreateContent from './SelectOrCreateContent';
import EventDetails from './EventDetails';
import RegistrationSetup from './RegistrationSetup';
import ImagesAndStyle from './ImagesAndStyles';
import EmailNotifications from './EmailNotifications';
import { useEventActions } from 'src/store/event/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'src/store/reducers/state';
import ToastMessage from 'src/components/ToastContainer';
import { successMsg } from 'src/components/utilities/utils';
import ReviewPublish from './ReviewPublish';
import { ContentTypeOption } from 'src/models/eventModel';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useVideosActions } from 'src/store/videos/actions';
const contentTypes: ContentTypeOption[] = [
    {
        id: 3,
        title: 'Video Play Page',
        description: 'A live or on-demand video displayed within your Content Library experience, with your site header, title, description, metadata and other page content.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="8" y1="13" x2="16" y2="13" />
                <line x1="8" y1="17" x2="16" y2="17" />
            </svg>
        )
    },
    {
        id: 1,
        title: 'Event Channel',
        description:
            'A channel containing multiple sessions or videos. Ideal for conferences, multi-session events or event content libraries.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
        )
    },
    {
        id: 2,
        title: 'Video Player Only',
        description:
            'A standalone live or on-demand video player. Attendees see only the video player, without the Content Library header, title, description or other page content.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="none" />
            </svg>
        )
    }
];
type locationState = {
    fromEventOverview: string;
};
const CreateEvent: React.FC = () => {
    const dispatch = useDispatch()
    const eventActions = useEventActions(dispatch)
    const videoActions = useVideosActions(dispatch)
    const navigate = useNavigate()
    const location = useLocation();
    const { fromEventOverview } = (location.state as locationState) || {};
    const { eventId } = useParams<string>();
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedContentType, setSelectedContentType] = useState<any>(3);
    const [newVideoData, setNewVideoData] = useState()
    const [channelName, setChannelName] = useState()
    const [eventTitle, setEventTitle] = useState()
    const [isFinalStep, setIsFinalStep] = useState(false)
    const [editEventClicked, setEditEventClicked] = useState(false);
    const {
        singleEvent,
        isEventUpdated,
        isEmailSettingUpdated
    } = useSelector((state: RootState) => {
        return {
            singleEvent: state.event.singleEvent,
            isEventUpdated: state.event.isEventUpdated,
            isEmailSettingUpdated: state.event.isEmailSettingUpdated
        };
    });
    useEffect(() => {
        if (eventId) {
            eventActions.getSingleEventRequest(eventId);
        }
    }, [eventId]);
    useEffect(() => {
        if (currentStep === 7 || fromEventOverview) {
            setIsFinalStep(true);
        }
    }, [currentStep, fromEventOverview]);

    useEffect(() => {
        if (singleEvent && Object.keys(singleEvent).length && singleEvent.selectedVideo)
            videoActions.getSingleVideosRequest(singleEvent.selectedVideo);
    }, [singleEvent])
    useEffect(() => {
        if (singleEvent && Object.keys(singleEvent).length) {
            setSelectedContentType(singleEvent.eventType == 0 ? 3 : singleEvent.eventType)
        }
    }, [singleEvent])

    useEffect(() => {
        if (isEventUpdated || isEmailSettingUpdated) {
            successMsg('success')
            eventActions.resetEventStateRequest()
        }
    }, [isEventUpdated, isEmailSettingUpdated])

    const submitData = () => {
        if (!singleEvent?.id) {
            return;
        }
        const patchJsonModel = {
            id: singleEvent.id,
            patchJsonData: [{ op: 'replace', path: 'eventType', value: selectedContentType }]
        };
        eventActions.updateEventRequest(patchJsonModel)
        setCurrentStep(2)
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <ToastMessage />
            <div className="flex flex-col md:flex-row flex-1 px-4 md:px-6 py-4 md:py-6 gap-4 md:gap-6">
                {/* Sidebar */}
                <EventSidebarMenu currentStep={currentStep} setCurrentStep={setCurrentStep} setEditEventClicked={setEditEventClicked} setIsFinalStep={setIsFinalStep} isFinalStep={isFinalStep} />
                {/* Main panel */}
                {
                    currentStep === 1 ? (
                        <main className="flex-1 flex flex-col min-w-0">
                            <div className="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col">
                                <div className="flex items-start justify-between gap-3 px-4 sm:px-8 pt-5 sm:pt-6">
                                    <div className="min-w-0">
                                        <h2 className="text-lg font-semibold text-gray-900 m-0">Event Setup</h2>
                                        <p className="text-sm text-gray-400 mt-1">{`Choose what your registration will link to, and select or create content.`}</p>
                                    </div>
                                    <button type="button" onClick={() => { navigate('/events/event') }} className="flex-shrink-0 flex items-center gap-1.5 text-sm font-semibold text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                        Exit
                                    </button>
                                </div>

                                {/* Content type selection */}
                                <div className="px-4 sm:px-8 mt-6 sm:mt-8 pb-6">
                                    <h3 className="text-sm font-semibold text-gray-900 m-0">What should attendees see after registering?</h3>
                                    <p className="text-sm text-gray-400 mt-1">Choose the viewing experience attendees will access after completing registration.</p>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                                        {contentTypes.map((option) => {
                                            const isSelected = selectedContentType === option.id;
                                            return (
                                                <button
                                                    key={option.id}
                                                    type="button"
                                                    onClick={() => setSelectedContentType(option.id)}
                                                    className={`relative text-left rounded-xl border-2 p-5 transition-colors ${isSelected ? 'border-indigo-600 bg-indigo-50/40' : 'border-gray-200 hover:border-gray-300'
                                                        }`}>
                                                    <span
                                                        className={`absolute top-4 right-4 w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-indigo-600' : 'border-gray-300'
                                                            }`}>
                                                        {isSelected && <span className="w-2 h-2 rounded-full bg-indigo-600" />}
                                                    </span>

                                                    <span
                                                        className={`flex items-center justify-center w-11 h-11 rounded-lg ${isSelected ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'
                                                            }`}>
                                                        {option.icon}
                                                    </span>

                                                    <p className={`text-sm font-semibold mt-4 mb-1.5 ${isSelected ? 'text-indigo-600' : 'text-gray-900'}`}>
                                                        {option.title}
                                                    </p>
                                                    <p className="text-xs text-gray-400 leading-relaxed m-0">{option.description}</p>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="flex items-start gap-2 bg-indigo-50 text-indigo-700 text-sm rounded-lg px-4 py-3 mt-6">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="16" x2="12" y2="12" />
                                            <line x1="12" y1="8" x2="12.01" y2="8" />
                                        </svg>
                                        {`You can select existing content in the next step, or create new content if it doesn’t exist yet.`}
                                    </div>
                                </div>

                                {/* <div className="flex-1" /> */}

                                {/* Footer */}
                                <div className="flex justify-end px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => submitData()}
                                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-5 py-2.5">
                                        Next: Select or Create Content
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                            <polyline points="12 5 19 12 12 19" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </main>
                    ) : currentStep === 2 ? (
                        <SelectOrCreateContent setCurrentStep={setCurrentStep} setNewVideoData={setNewVideoData} eventTitle={eventTitle} newVideoData={newVideoData} selectedContentType={selectedContentType} setChannelName={setChannelName} channelName={channelName} />
                    ) : currentStep === 3 ? (
                        <EventDetails editEventClicked={editEventClicked} newVideoData={newVideoData} setCurrentStep={setCurrentStep} />
                    ) : currentStep === 4 ? (
                        <RegistrationSetup setCurrentStep={setCurrentStep} />
                    ) : currentStep === 5 ? (
                        <ImagesAndStyle setCurrentStep={setCurrentStep} />
                    ) : currentStep === 6 ? (
                        <EmailNotifications editEventClicked={editEventClicked} setCurrentStep={setCurrentStep} />
                    ) : currentStep === 7 ? (
                        <ReviewPublish setCurrentStep={setCurrentStep} channelName={channelName} />
                    ) : (<></>)
                }

            </div>
        </div>
    );
};

export default CreateEvent;